import type { PackagingToDistributionReadinessCheck } from '../types';
import type { RaceConfigurationRequirements } from '../../../helpers/types/DeviceValidationTypes';

/** Manual operator checklist for Packaging-stage device commands. */
export type PackagingCommandStepKey =
  | 'firmwareDownload'
  | 'raceLogClear'
  | 'debugLogClear'
  | 'frontFirmwareDownload'
  | 'frontRaceLogClear'
  | 'frontDebugLogClear';

export type PackagingCommandChecklistState = Record<PackagingCommandStepKey, boolean>;

export type PackagingCommandStepDefinition = {
  key: PackagingCommandStepKey;
  label: string;
  description: string;
  blocking: boolean;
};

export const PACKAGING_COMMAND_STEPS: PackagingCommandStepDefinition[] = [
  {
    key: 'firmwareDownload',
    label: 'Firmware download sent',
    description: 'Submit FW_DWNLD for all rear devices using the race configuration firmware version.',
    blocking: true,
  },
  {
    key: 'raceLogClear',
    label: 'Race logs cleared',
    description:
      'Submit DELETE_LOGS (race) for all rear devices. Optionally run LOG_UPLD (race) and download logs before delete.',
    blocking: true,
  },
  {
    key: 'debugLogClear',
    label: 'Debug logs cleared',
    description:
      'Submit DELETE_LOGS (debug) for all rear devices. Optionally run LOG_UPLD (debug) and download logs before delete.',
    blocking: true,
  },
  {
    key: 'frontFirmwareDownload',
    label: 'Front firmware download sent',
    description: 'Submit FRONT_FW_DWNLD for all front devices using the race configuration front firmware version.',
    blocking: true,
  },
  {
    key: 'frontRaceLogClear',
    label: 'Front race logs cleared',
    description:
      'Submit FRONT_DELETE_LOGS (race) for all front devices. Optionally run FRONT_LOG_UPLD (race) and download logs before delete.',
    blocking: true,
  },
  {
    key: 'frontDebugLogClear',
    label: 'Front debug logs cleared',
    description:
      'Submit FRONT_DELETE_LOGS (debug) for all front devices. Optionally run FRONT_LOG_UPLD (debug) and download logs before delete.',
    blocking: true,
  },
];

export function emptyPackagingCommandChecklist(): PackagingCommandChecklistState {
  return {
    firmwareDownload: false,
    raceLogClear: false,
    debugLogClear: false,
    frontFirmwareDownload: false,
    frontRaceLogClear: false,
    frontDebugLogClear: false,
  };
}

export function packagingCommandChecklistStorageKey(eventId: string, raceId: string): string {
  return `fleet-packaging-command-checklist:${String(eventId ?? '').trim()}:${String(raceId ?? '').trim()}`;
}

export function readPackagingCommandChecklist(
  eventId: string,
  raceId: string,
): PackagingCommandChecklistState {
  try {
    const raw = sessionStorage.getItem(packagingCommandChecklistStorageKey(eventId, raceId));
    if (!raw) return emptyPackagingCommandChecklist();
    const parsed = JSON.parse(raw) as Partial<PackagingCommandChecklistState>;
    const base = emptyPackagingCommandChecklist();
    for (const step of PACKAGING_COMMAND_STEPS) {
      base[step.key] = parsed[step.key] === true;
    }
    return base;
  } catch {
    return emptyPackagingCommandChecklist();
  }
}

export function writePackagingCommandChecklist(
  eventId: string,
  raceId: string,
  state: PackagingCommandChecklistState,
): void {
  try {
    sessionStorage.setItem(
      packagingCommandChecklistStorageKey(eventId, raceId),
      JSON.stringify(state),
    );
  } catch {
    /* sessionStorage unavailable */
  }
}

export function clearPackagingCommandChecklist(eventId: string, raceId: string): void {
  try {
    sessionStorage.removeItem(packagingCommandChecklistStorageKey(eventId, raceId));
  } catch {
    /* ignore */
  }
}

export function togglePackagingCommandStep(
  state: PackagingCommandChecklistState,
  key: PackagingCommandStepKey,
): PackagingCommandChecklistState {
  return { ...state, [key]: !state[key] };
}

export function countCompletedPackagingCommandSteps(state: PackagingCommandChecklistState): number {
  return PACKAGING_COMMAND_STEPS.filter((step) => state[step.key]).length;
}

const COMMAND_STEP_KEYS = new Set(PACKAGING_COMMAND_STEPS.map((s) => s.key));

export function isPackagingCommandReadinessCheckKey(key: string): boolean {
  return COMMAND_STEP_KEYS.has(key as PackagingCommandStepKey);
}

/** True when manual device-command checklist items in a readiness list are all satisfied. */
export function arePackagingCommandReadinessChecksPass(
  checks: PackagingToDistributionReadinessCheck[],
): boolean {
  const relevant = checks.filter((c) => COMMAND_STEP_KEYS.has(c.key as PackagingCommandStepKey));
  if (relevant.length === 0) return true;
  return relevant.every((c) => c.ok || c.blocking === false);
}

export function areAllPackagingCommandStepsComplete(state: PackagingCommandChecklistState): boolean {
  return PACKAGING_COMMAND_STEPS.every((step) => {
    if (!step.blocking) return true;
    return state[step.key] === true;
  });
}

export function computePackagingDeviceCommandChecks(input: {
  checklist: PackagingCommandChecklistState | null | undefined;
  raceConfig: RaceConfigurationRequirements | null;
}): PackagingToDistributionReadinessCheck[] {
  const checklist = input.checklist ?? emptyPackagingCommandChecklist();
  const fwLabel = String(input.raceConfig?.fw_version ?? '').trim();
  const fwFrontLabel = String(input.raceConfig?.fw_version_front ?? '').trim();

  return PACKAGING_COMMAND_STEPS.map((step) => {
    const ok = checklist[step.key] === true;
    let detail: string | undefined;
    if (!ok) {
      detail = 'Tick the checkbox in Device commands after you have submitted this command.';
    }
    if (step.key === 'firmwareDownload' && fwLabel) {
      detail = ok
        ? `Firmware target: ${fwLabel}`
        : `Send FW_DWNLD targeting ${fwLabel}, then tick the checkbox in Device commands.`;
    }
    if (step.key === 'frontFirmwareDownload' && fwFrontLabel) {
      detail = ok
        ? `Front firmware target: ${fwFrontLabel}`
        : `Send FRONT_FW_DWNLD targeting ${fwFrontLabel}, then tick the checkbox in Device commands.`;
    }
    return {
      key: step.key,
      label: step.label,
      ok,
      blocking: step.blocking,
      state: ok ? 'pass' : 'fail',
      detail,
    };
  });
}
